<template>
  <ModalStep
    id="user-profile-modal-content"
    :header="{
      title: `${userRef?.firstName} ${userRef?.lastName}`,
    }"
    :on-exit-handler="onExitHandler"
  >
    <template #bodyInformation>
      <LoadingContent v-if="isUserPendingRef || isAccountPendingRef" :text="t('ui.message.loading')" />
    </template>
    <template #bodyContent v-if="userRef">
      <div class="user-profile__info-card">
        <div class="user-profile__info-card__avatar">
          <Avatar :user="userRef" />
        </div>
        <div class="user-profile__info-card__info">
          <p class="info-name">{{ userRef.firstName }} {{ userRef.lastName }}</p>
          <div
            class="user-profile__info-card__info__roles-container"
            v-if="
              userRef.roles &&
              userRef.roles.length > 0 &&
              userActivePermissionGroupRef === PermissionsGroupsEnumType.ADMIN
            "
          >
            <p class="info-role" v-for="role in userRef.roles" :key="role">{{ t(userRolesNamesMap[role]) }}</p>
          </div>
        </div>
      </div>
      <div
        class="c-info-wrapper"
        v-if="canUserSeeNoteRef && userActivePermissionGroupRef === PermissionsGroupsEnumType.ADMIN"
      >
        <p class="c-info-label">
          <strong>{{ t('user.profile.sharedNotes.title') }}</strong>
        </p>
        <p class="c-info-label__subtext">
          {{ t('user.profile.sharedNotes') }}
        </p>
        <CustomTextarea
          ref="userProfileNoteTextAreaRef"
          id="user-profile-note"
          :label="t('user.profile.sharedNotes.title')"
          hide-label
          :placeholder="t('user.profile.sharedNotes.add.placeholder')"
          :value="userNotesRef"
          @emit:change="changeNoteHandler"
        />
      </div>
      <div class="c-info-wrapper">
        <p class="c-info-label">
          <strong>{{ t('user.profile.description') }}</strong>
        </p>
        <div
          v-if="hasDescriptionRef"
          class="user-profile__description"
          id="user-profile-description"
          data-test-id="user-profile-description"
        ></div>
        <p v-else class="c-info-text user-profile__description">
          {{ t('user.profile.description.empty') }}
        </p>
      </div>
      <div></div>
      <!-- Critères de matching Mentor -->
      <div
        v-if="userMentorCriteriaRef && userMentorCriteriaRef.length > 0 && checkPermissionsGroup(programRolesRef, PermissionsGroupsEnumType.ADMIN)"
        class="c-info-wrapper"
      >
        <p class="c-info-label">
          <strong>{{ t('user.profile.mentorMatchingCriteria') }}</strong>
        </p>
        <p
          data-test-id="user-profile-mentor-matchingCriteria"
          v-html="fillTemplate(mentorMatchingTemplateRef ? mentorMatchingTemplateRef : '', userMentorCriteriaRef)"
        ></p>
      </div>
      <!-- Critères de matching Mentee -->
      <div
        v-if="userMenteeCriteriaRef && userMenteeCriteriaRef.length > 0 && checkPermissionsGroup(programRolesRef, PermissionsGroupsEnumType.ADMIN)"
        class="c-info-wrapper"
      >
        <p class="c-info-label">
          <strong>{{ t('user.profile.menteeMatchingCriteria') }}</strong>
        </p>
        <p
          data-test-id="user-profile-mentee-matchingCriteria"
          v-html="fillTemplate(menteeMatchingTemplateRef ? menteeMatchingTemplateRef : '', userMenteeCriteriaRef)"
        ></p>
      </div>
    </template>
    <template #footer>
      <div class="c-btns-container c-btn-center">
        <CustomButton
          v-if="canUserSeeNoteRef && userActivePermissionGroupRef === PermissionsGroupsEnumType.ADMIN"
          :is-disabled="userNotesRef === (userNoteDataRef?.content || null)"
          :is-loading="isUpdateUserNotesPendingRef"
          usage="button"
          type="button"
          color="primary"
          :text="t('user.profile.sharedNotes.saveChanges')"
          @emit:click="saveChanges"
        >
          <template #icon>
            <!-- <EditIcon color="white" filled/> -->
          </template>
        </CustomButton>
        <CustomButton
          v-if="accountRef?.id === userRef?.id"
          usage="button"
          type="button"
          color="primary"
          :text="t('user.profile.edit')"
          @emit:click="goToProfile"
        >
          <template #icon>
            <!-- <EditIcon color="white" filled/> -->
          </template>
        </CustomButton>
        <CustomButton
          v-else-if="
            checkPermissionsGroup(programRolesRef, PermissionsGroupsEnumType.USER) &&
            userActivePermissionGroupRef === PermissionsGroupsEnumType.USER
          "
          usage="button"
          type="button"
          color="primary"
          :text="t('user.profile.sendMessage')"
          @emit:click="goToMessage"
        >
          <template #icon>
            <MessageIcon color="white" filled />
          </template>
        </CustomButton>
      </div>
    </template>
  </ModalStep>
</template>

<script setup lang="ts">
import { useMutation, useQuery } from '@tanstack/vue-query'
import { computed } from 'vue'
import { ref } from 'vue'
import { watch } from 'vue'

import { GetUserNotes, UpdateUserNotesMutation, USER_NOTE_QUERY_KEY } from '@/features/admin/users/api'
import useAccount from '@/features/auth/hooks/use-account.hook'
import { selectGetUserActivePermissionGroup } from '@/features/auth/stores/auth/auth.selectors'
import authStore from '@/features/auth/stores/auth/auth.store'
import { PermissionsGroupsEnumType } from '@/features/auth/types'
import useMatchingInformation from '@/features/matching/hooks/use-matching-information.hook'
import { selectGetSelectedProgramId } from '@/features/programs/stores/programs/programs.selectors'
import programsStore from '@/features/programs/stores/programs/programs.store'
import CustomButton from '@/features/ui/components/button/custom-button.vue'
import CustomTextarea from '@/features/ui/components/custom-textarea/custom-textarea.vue'
import MessageIcon from '@/features/ui/components/icons/message-icon.vue'
import LoadingContent from '@/features/ui/components/loading-content/loading-content.vue'
import ModalStep from '@/features/ui/components/modal/modal-step/modal-step.vue'
import { selectGetUserProfileModal } from '@/features/ui/store/modal/modal.selectors'
import type { UserProfileModalType } from '@/features/ui/store/modal/modal.store'
import modalsStore from '@/features/ui/store/modal/modal.store'
import Avatar from '@/features/users/components/avatar/avatar.vue'
import useProfileDescription from '@/features/users/hooks/use-profile-description.hook'
import useProgramRoles from '@/features/users/hooks/use-program-roles.hook'
import useUser from '@/features/users/hooks/use-user.hook'
import checkPermissionsGroup, { userRolesNamesMap } from '@/features/users/utils'
import useRouter from '@/hooks/use-router.hook'
import useToast from '@/hooks/use-toasts.hook'
import { useI18n } from '@/lib/i18n'
import queryClient from '@/lib/vue-query'
import { ROUTES } from '@/utils/config/constants'
import { invalidateConversations } from '@/utils/lib/vue-query'

defineProps<{
  onExitHandler: (callback?: () => void) => void
}>()

const { t } = useI18n()

const userProfileModal = selectGetUserProfileModal(modalsStore)
const attachedDataUserIdRef = computed(() => (userProfileModal?.value as UserProfileModalType).attachedData?.userId)

const { accountRef, isPendingRef: isAccountPendingRef } = useAccount()
const { userRef, isPendingRef: isUserPendingRef } = useUser(attachedDataUserIdRef)
const { hasDescriptionRef } = useProfileDescription(attachedDataUserIdRef, 'user-profile-description')
const { programRolesRef } = useProgramRoles()
const userActivePermissionGroupRef = selectGetUserActivePermissionGroup(authStore)

const { mentorMatchingTemplateRef, menteeMatchingTemplateRef, fillTemplate, extractUUIDs } = useMatchingInformation({
  onSubmitCallback: () => null,
})

// Filtrer les critères de l'utilisateur consulté par type (mentor/mentee)
const userMentorCriteriaRef = computed(() => {
  if (!userRef.value?.matchingCriteria || !mentorMatchingTemplateRef.value) return null
  const mentorUUIDs = extractUUIDs(mentorMatchingTemplateRef.value)
  return userRef.value.matchingCriteria.filter((c: { inputId: string }) => mentorUUIDs.includes(c.inputId))
})

const userMenteeCriteriaRef = computed(() => {
  if (!userRef.value?.matchingCriteria || !menteeMatchingTemplateRef.value) return null
  const menteeUUIDs = extractUUIDs(menteeMatchingTemplateRef.value)
  return userRef.value.matchingCriteria.filter((c: { inputId: string }) => menteeUUIDs.includes(c.inputId))
})

const router = useRouter()
const toast = useToast()
const selectedProgramIdRef = selectGetSelectedProgramId(programsStore)

async function goToMessage() {
  const userId = userRef.value?.id
  if (userId && selectedProgramIdRef.value) {
    router?.push(`${ROUTES.CHAT()}?sendTo=${userId}&programId=${selectedProgramIdRef.value}`)
  }
}

function goToProfile() {
  const userId = userRef.value?.id
  if (userId) {
    router?.push(ROUTES.PROFILE)
  }
}

const userProfileNoteTextAreaRef = ref<any | null>(null)
const userNotesRef = ref<string | null>(null)

// if the user is an admin and the account is not the user, show the note
const canUserSeeNoteRef = computed(() => {
  return (
    !!userRef.value &&
    accountRef.value?.id !== userRef.value?.id &&
    checkPermissionsGroup(programRolesRef.value, PermissionsGroupsEnumType.ADMIN)
  )
})

const { data: userNoteDataRef } = useQuery({
  queryKey: [USER_NOTE_QUERY_KEY, { userId: attachedDataUserIdRef.value! }],
  queryFn: () => GetUserNotes({ userId: attachedDataUserIdRef.value! }),
  enabled: canUserSeeNoteRef,
})

// watch for changes in the user note data
watch(
  [userNoteDataRef, userProfileNoteTextAreaRef],
  () => {
    const textArea = userProfileNoteTextAreaRef.value?.$el.querySelector('textarea') as HTMLTextAreaElement

    // if the user has a note, set the note in the textarea
    if (textArea && userNoteDataRef.value?.content) {
      userNotesRef.value = userNoteDataRef.value?.content
      textArea.value = userNoteDataRef.value?.content
    }
  },
  { immediate: true },
)

const { mutate: updateUserNotes, isPending: isUpdateUserNotesPendingRef } = useMutation({
  mutationFn: UpdateUserNotesMutation,
  onSuccess: () => {
    toast?.success(t('user.profile.sharedNotes.success'))
    // remove the query from the cache
    queryClient.invalidateQueries({
      queryKey: [USER_NOTE_QUERY_KEY, { userId: attachedDataUserIdRef.value! }],
    })
  },
  onError: () => {
    toast?.error(t('error.common'))
  },
})

function changeNoteHandler(value: string) {
  userNotesRef.value = value
}

function saveChanges() {
  updateUserNotes({ userId: attachedDataUserIdRef.value!, content: userNotesRef.value || '' })
}
</script>

<style lang="scss">
@import './user-profile-modal-content.scss';
</style>
